import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../Data/AuthContext";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/program", label: "Programs" },
  { to: "/events", label: "Events" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function initials(user) {
  if (!user) return "?";
  const first = (user.firstname || "").trim();
  const last = (user.lastname || "").trim();
  if (first || last) return `${first[0] || ""}${last[0] || ""}`.toUpperCase();
  return (user.email || "?")[0].toUpperCase();
}

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Any navigation closes both overlays, otherwise the mobile sheet stays
  // open on top of the page the user just asked for.
  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setNavOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/");
  }

  return (
    <header className="es-header">
      <a className="es-skip-link" href="#main-content">
        Skip to main content
      </a>

      <div className="es-header__inner">
        <Link to="/" className="es-brand">
          <span className="es-brand__mark" aria-hidden="true">ES</span>
          <span className="es-brand__name">
            <strong>Edu</strong><span>Special</span>
          </span>
        </Link>

        <button
          type="button"
          className="es-burger"
          aria-label={navOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={navOpen}
          aria-controls="primary-navigation"
          onClick={() => setNavOpen((v) => !v)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            {navOpen ? (
              <>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </>
            ) : (
              <>
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </>
            )}
          </svg>
        </button>

        <nav
          id="primary-navigation"
          className="es-nav"
          data-open={navOpen}
          aria-label="Primary"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="es-nav__link"
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="es-header__actions">
          <Link to="/donate" className="es-btn es-btn--primary es-btn--sm">
            Donate
          </Link>

          {!isAuthenticated ? (
            <Link to="/signin" className="es-btn es-btn--ghost es-btn--sm">
              Sign in
            </Link>
          ) : (
            <div style={{ position: "relative" }} ref={menuRef}>
              <button
                type="button"
                className="es-avatar"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`Account menu for ${user?.firstname || user?.email || "your account"}`}
                onClick={() => setMenuOpen((v) => !v)}
              >
                {initials(user)}
              </button>

              {menuOpen && (
                <div className="es-menu" role="menu">
                  <div className="es-menu__label">
                    <div style={{ fontWeight: 600, color: "var(--es-ink)" }}>
                      {user?.firstname} {user?.lastname}
                    </div>
                    <div className="es-muted" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                      {user?.email}
                    </div>
                  </div>

                  <Link className="es-menu__item" role="menuitem" to="/userprofile">
                    Your profile
                  </Link>

                  {user?.role === "admin" && (
                    <Link className="es-menu__item" role="menuitem" to="/AdminDash">
                      Admin dashboard
                    </Link>
                  )}

                  <button
                    type="button"
                    className="es-menu__item es-menu__item--danger"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
